import React, { useCallback, useState, createContext, useContext } from 'react';
// Types
export type ApprovalStep = {
  role: string;
  name: string;
  status: 'approved' | 'pending' | 'waiting';
};
export type TravelRequest = {
  id: string;
  requester: string;
  role: string;
  trip: string;
  destination: string;
  dates: string;
  amount: number;
  type: 'flight' | 'hotel' | 'car';
  details: string;
  status: 'pending' | 'approved' | 'rejected';
  policyStatus: 'compliant' | 'violation';
  submittedAt: string;
  chain: ApprovalStep[];
  policyFlags?: string[];
};
export type Notification = {
  id: number;
  type: 'approval' | 'booking' | 'policy' | 'system';
  title: string;
  desc: string;
  time: string;
  read: boolean;
  action: string | null;
};
export type ConfirmedTrip = {
  id: number;
  title: string;
  location: string;
  dates: string;
  status: 'Confirmed';
  totalCost: string;
};
interface AppContextType {
  travelRequests: TravelRequest[];
  notifications: Notification[];
  confirmedTrips: ConfirmedTrip[];
  submitBooking: (booking: any) => void;
  approveRequest: (requestId: string) => void;
  rejectRequest: (requestId: string) => void;
  markNotificationRead: (id: number) => void;
  markAllNotificationsRead: () => void;
}
const AppContext = createContext<AppContextType | undefined>(undefined);
export function AppProvider({
  children
}: {
  children: ReactNode;
}) {
  // Initial State
  const [travelRequests, setTravelRequests] = useState<TravelRequest[]>([{
    id: 'REQ-2024-001',
    requester: 'David Miller',
    role: 'Senior Developer',
    trip: 'Tech Conference 2024',
    destination: 'San Francisco, CA',
    dates: 'Nov 12 - Nov 15',
    amount: 1850,
    type: 'flight',
    details: 'United Airlines',
    status: 'pending',
    policyStatus: 'violation',
    policyFlags: ['Flight cost exceeds cap by 10%'],
    submittedAt: '2 hours ago',
    chain: [{
      role: 'Direct Manager',
      name: 'Sarah Connor',
      status: 'approved'
    }, {
      role: 'Dept Head',
      name: 'Alex Morgan',
      status: 'pending'
    }, {
      role: 'Finance',
      name: 'Emily Chen',
      status: 'waiting'
    }]
  }, {
    id: 'REQ-2024-002',
    requester: 'Sarah Jenkins',
    role: 'Sales Director',
    trip: 'Client Meeting',
    destination: 'Chicago, IL',
    dates: 'Nov 18 - Nov 20',
    amount: 950,
    type: 'hotel',
    details: 'Marriott Downtown',
    status: 'pending',
    policyStatus: 'compliant',
    policyFlags: [],
    submittedAt: '5 hours ago',
    chain: [{
      role: 'Direct Manager',
      name: 'Mike Ross',
      status: 'pending'
    }, {
      role: 'Dept Head',
      name: 'Jessica Pearson',
      status: 'waiting'
    }, {
      role: 'Finance',
      name: 'Louis Litt',
      status: 'waiting'
    }]
  }, {
    id: 'REQ-2024-003',
    requester: 'Michael Chen',
    role: 'Product Manager',
    trip: 'Team Offsite',
    destination: 'Austin, TX',
    dates: 'Dec 05 - Dec 08',
    amount: 1200,
    type: 'flight',
    details: 'Delta Airlines',
    status: 'approved',
    policyStatus: 'compliant',
    policyFlags: [],
    submittedAt: '1 day ago',
    chain: [{
      role: 'Direct Manager',
      name: 'Sarah Connor',
      status: 'approved'
    }, {
      role: 'Dept Head',
      name: 'Alex Morgan',
      status: 'approved'
    }, {
      role: 'Finance',
      name: 'Emily Chen',
      status: 'approved'
    }]
  }]);
  const [notifications, setNotifications] = useState<Notification[]>([{
    id: 1,
    type: 'approval',
    title: 'Trip request approved',
    desc: 'Your request for Tech Conference has been approved by Sarah Connor',
    time: '2 hours ago',
    read: false,
    action: 'View Request'
  }, {
    id: 2,
    type: 'booking',
    title: 'New booking confirmed',
    desc: 'Flight UA 4521 JFK→LHR confirmed for Nov 12',
    time: '5 hours ago',
    read: false,
    action: 'View Booking'
  }, {
    id: 3,
    type: 'policy',
    title: 'Policy violation detected',
    desc: 'Hotel booking exceeds nightly cap by $45',
    time: '1 day ago',
    read: true,
    action: 'Review Policy'
  }, {
    id: 4,
    type: 'system',
    title: 'Expense report approved',
    desc: 'Q3 Travel Expenses ($3,240) approved for reimbursement',
    time: '1 day ago',
    read: true,
    action: 'View Report'
  }]);
  const [confirmedTrips, setConfirmedTrips] = useState<ConfirmedTrip[]>([{
    id: 1,
    title: 'Q4 Strategy Meeting',
    location: 'San Francisco, CA',
    dates: 'Nov 12 - Nov 15, 2023',
    status: 'Confirmed',
    totalCost: '$2,003.00'
  }]);
  // Actions
  const submitBooking = useCallback((booking: any) => {
    const newRequest: TravelRequest = {
      id: `REQ-2024-${Math.floor(Math.random() * 1000)}`,
      requester: 'Current User',
      role: 'Employee',
      trip: booking.name || booking.model || booking.airline || 'New Trip',
      destination: booking.destination || 'San Francisco, CA',
      dates: 'Upcoming',
      amount: booking.price,
      type: booking.type || 'flight',
      details: booking.name || booking.model || booking.airline,
      status: 'pending',
      policyStatus: booking.policy === 'compliant' ? 'compliant' : 'violation',
      policyFlags: booking.policy === 'violation' ? ['Policy violation detected'] : [],
      submittedAt: 'Just now',
      chain: [{
        role: 'Direct Manager',
        name: 'Sarah Connor',
        status: 'pending'
      }, {
        role: 'Dept Head',
        name: 'Alex Morgan',
        status: 'waiting'
      }, {
        role: 'Finance',
        name: 'Emily Chen',
        status: 'waiting'
      }]
    };
    setTravelRequests(prev => [newRequest, ...prev]);
    // Add notification
    const newNotification: Notification = {
      id: Date.now(),
      type: 'booking',
      title: 'New booking submitted',
      desc: `Booking for ${newRequest.trip} submitted for approval`,
      time: 'Just now',
      read: false,
      action: 'View Request'
    };
    setNotifications(prev => [newNotification, ...prev]);
  }, []);
  const approveRequest = useCallback((requestId: string) => {
    setTravelRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      // Clone chain to avoid mutation
      const newChain = [...req.chain];
      // Find first pending step
      const pendingIndex = newChain.findIndex(step => step.status === 'pending');
      if (pendingIndex !== -1) {
        // Approve current step
        newChain[pendingIndex] = {
          ...newChain[pendingIndex],
          status: 'approved'
        };
        // Activate next step if exists
        if (pendingIndex + 1 < newChain.length) {
          newChain[pendingIndex + 1] = {
            ...newChain[pendingIndex + 1],
            status: 'pending'
          };
        }
      }
      // Check if all approved
      const allApproved = newChain.every(step => step.status === 'approved');
      const newStatus = allApproved ? 'approved' : 'pending';
      // If fully approved, add to confirmed trips and notify
      if (allApproved && req.status !== 'approved') {
        const newTrip: ConfirmedTrip = {
          id: Date.now(),
          title: req.trip,
          location: req.destination,
          dates: req.dates,
          status: 'Confirmed',
          totalCost: `$${req.amount}`
        };
        setConfirmedTrips(prevTrips => [newTrip, ...prevTrips]);
        const newNotification: Notification = {
          id: Date.now(),
          type: 'approval',
          title: 'Trip Approved',
          desc: `Your trip to ${req.destination} has been fully approved`,
          time: 'Just now',
          read: false,
          action: 'View Trip'
        };
        setNotifications(prevNotifs => [newNotification, ...prevNotifs]);
      }
      return {
        ...req,
        chain: newChain,
        status: newStatus
      };
    }));
  }, []);
  const rejectRequest = useCallback((requestId: string) => {
    setTravelRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      return {
        ...req,
        status: 'rejected'
      };
    }));
    const newNotification: Notification = {
      id: Date.now(),
      type: 'approval',
      title: 'Trip Rejected',
      desc: `A travel request has been rejected`,
      time: 'Just now',
      read: false,
      action: 'View Request'
    };
    setNotifications(prev => [newNotification, ...prev]);
  }, []);
  const markNotificationRead = useCallback((id: number) => {
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  }, []);
  const markAllNotificationsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      read: true
    })));
  }, []);
  return <AppContext.Provider value={{
    travelRequests,
    notifications,
    confirmedTrips,
    submitBooking,
    approveRequest,
    rejectRequest,
    markNotificationRead,
    markAllNotificationsRead
  }} data-id="element-143">
      {children}
    </AppContext.Provider>;
}
export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}